<script lang="ts">
  import ky, { HTTPError } from "ky";
  import { v4 as uuidv4 } from "uuid";
  import { push } from "svelte-spa-router";
  import { get } from "svelte/store";
  import { alias, track } from "../helpers/mixpanel";
  import { store, tempToken } from "../helpers/store";

  import LoginLhs from "../components/LoginLhs.svelte";
  import Stack from "../components/Stack.svelte";
  import Text from "../components/Text.svelte";
  import Button from "../components/Button.svelte";
  import CodeInput from "../components/CodeInput.svelte";
  import PhoneInput from "../components/PhoneInput.svelte";
  import EmailLoginInput from "../components/EmailLoginInput.svelte";
  import LoadingSpinner from "../components/LoadingSpinner.svelte";
  import { getRekkiEnvironment } from "../helpers/environment";
  import { getLanding } from "../helpers/landing";
  import { createGetSubscriptionQuery } from "../queries/subscriptions";
  import { getInitialSubscriptionModalRedirectForUserSession } from "../helpers/subscription";
  import { selectedBuyer } from "../stores/buyer";

  const ENV_PATH = getRekkiEnvironment();
  const AUTH_URL = `https://hulk.${ENV_PATH}.eu-west-2.rekki.com/backend/api/v2/auth/`;

  let distinctId: string = uuidv4();
  let username: { type: "email" | "phone"; value: string } | undefined;
  let code: string;
  let clickCount = 0;
  let resendCodeTimer = 0;
  let codeError: string | null = null;

  $: subscriptionQuery = createGetSubscriptionQuery($selectedBuyer?.id ?? "");

  function usernameInsideOut() {
    if (username) {
      const { type, value } = username;

      return { [type]: value };
    }
  }

  let requestingLoginCode = false;
  async function requestLoginCode() {
    if (requestingLoginCode) return;

    const user = username?.value;

    if (!user) {
      alert("Enter a valid username to continue.");
      return;
    }

    track("login_continue_cta", usernameInsideOut(), distinctId);

    try {
      requestingLoginCode = true;
      const { status } = await ky
        .post(AUTH_URL + "users/check_availability", { json: { username: user } })
        .json<any>();

      const override = get(store).loginOverride;

      switch (status) {
        case "not_available":
          const { temporary_token } = await ky
            .post(AUTH_URL + "codes/request", {
              json: { username: user, distinct_id: distinctId, override },
            })
            .json<any>();

          tempToken.set(temporary_token);
          return;
        case "not_valid_username":
        case "available":
          alert(`Username ${user} is invalid, please verify it is correct or contact support.`);
          break;
      }
    } catch (e) {
      alert("An error occurred, please try again or contact support.");
      console.error("Error during username check and code request", e);
    } finally {
      requestingLoginCode = false;
    }
  }

  function calculateTokenExpiration(token: string | null): number | null {
    if (token) {
      const fifteenDaysInMilliseconds = 15 * 24 * 60 * 60 * 1000;
      const expiration = Date.now() + fifteenDaysInMilliseconds;
      return expiration;
    }

    return null;
  }

  async function validateCode() {
    try {
      const { token: newToken, user: newUser } = await ky
        .post(AUTH_URL + "codes/validate", {
          json: {
            device: {
              accepts_push_notif: false,
              app_version: 0,
              device_model: "web",
              device_token: "unknown",
              device_type: "web",
              operating_system: "unity-web",
            },
            distinct_id: distinctId,
            temporary_token: $tempToken,
            code,
            shared_secret: "RFxkx2csAERxamLXHVOrZlBoaktXQ108",
          },
        })
        .json<any>();

      if (newToken) {
        store.update((s) => ({
          ...s,
          user: { ...newUser },
          token: newToken,
          tokenExpires: calculateTokenExpiration(newToken),
        }));
        alias(newUser.id);
        track("login_verify_code_cta", usernameInsideOut());

        // ensure we've loaded various stuff important on landing before we redirect.
        // ... before we null tempToken, which causes the code entry to disappear and weird flashing
        // occurs.
        // DOM TODO: refactor all this to use svelte-query
        await prefetchRequiredData();

        tempToken.set(null);

        let targetRoute = $store.originalRoute ? $store.originalRoute.toString() : "/spend-report";

        const redirect = getInitialSubscriptionModalRedirectForUserSession();
        if (redirect && ENV_PATH === "feat") {
          targetRoute = redirect;
        }

        push(targetRoute);
      }
    } catch (err) {
      console.error("error validating code", err);
      codeError = "invalid code, double check and try again";
    }
  }

  async function prefetchRequiredData() {
    try {
      await getLanding();
      if ($selectedBuyer?.id) {
        $subscriptionQuery.refetch();
      }
    } catch (error) {
      if (error instanceof HTTPError && error.response.status !== 402) {
        console.error(error);
      }
    }
  }

  function startResendCodeCounter() {
    var i = 60;
    setInterval(() => {
      if (i >= 0) {
        resendCodeTimer = i;
        i--;
        console.log(resendCodeTimer);
      }
    }, 1000);
  }

  function handleHeaderClick() {
    clickCount = clickCount + 1;

    if (clickCount > 7) {
      alert("SMS suppress code activated. Please check slack inbox channel for code");

      store.update((s) => ({
        ...s,
        loginOverride: "suppress_code_delivery",
      }));
    }
  }

  function setEmailLogin(e: CustomEvent<{ address: string }>) {
    const { address } = e.detail;
    username = {
      type: "email",
      value: address,
    };
  }

  function handlePhoneUpdated(e: CustomEvent<{ phone: string }>) {
    const { phone } = e.detail;
    username = {
      type: "phone",
      value: phone,
    };
  }

  function handleCodeEntered(value: string) {
    code = value;
    validateCode();
  }
</script>

<div class="container">
  <Stack horizontal justifyContent="stretch" alignItems="stretch">
    <div class="inner-container left">
      <LoginLhs onHeaderClick={handleHeaderClick}>
        {#if $tempToken}
          <Stack vertical spacing="medium" alignItems="stretch">
            <div class="form-container">
              <Stack vertical alignItems="stretch">
                <div class="code-input">
                  <CodeInput error={codeError} didComplete={(code) => handleCodeEntered(code)} />
                </div>

                <div class="helper-text">
                  <!--
                  kinda awkward username? here, but once we're here we've requested a token and
                  hidden the login inputs so it can't realistically change
                -->
                  <Text size="tiny" color="transparency-dark" ocd
                    >{`we've sent the code to ${username?.value}.`}</Text
                  >
                  {#if resendCodeTimer}
                    <div class="resend-timer">
                      <Text size="tiny" color="transparency-dark" ocd
                        >{`code sent, retry in ${resendCodeTimer}s`}</Text
                      >
                    </div>
                  {:else}
                    <div
                      class="resend"
                      on:click={() => {
                        track("login_resend_code_cta", usernameInsideOut());
                        requestLoginCode();
                        startResendCodeCounter();
                      }}
                      on:keypress={(e) => {
                        if (e.key === "Enter") {
                          track("login_resend_code_cta", usernameInsideOut());
                          requestLoginCode();
                          startResendCodeCounter();
                        }
                      }}
                    >
                      <Text size="tiny" color="transparency-dark" ocd>resend code</Text>
                    </div>
                  {/if}
                </div>
              </Stack>
            </div>
          </Stack>
        {:else}
          <Stack vertical spacing="medium" alignItems="stretch">
            <div class="form-container">
              <Stack vertical>
                <div class="username-type-select" class:email={username?.type == "email"}>
                  <button
                    on:click={() => {
                      username = { type: "phone", value: "" };
                    }}
                    class:active={username?.type == "phone"}>phone number</button
                  >
                  <button
                    on:click={() => {
                      username = { type: "email", value: "" };
                    }}
                    class:active={username?.type == "email"}>email</button
                  >
                </div>
                {#if !username || username.type !== "email"}
                  <div class="phone-input">
                    <PhoneInput on:update={handlePhoneUpdated} on:submit={requestLoginCode} />
                  </div>
                {:else}
                  <div class="phone-input">
                    <EmailLoginInput on:update={setEmailLogin} />
                  </div>
                {/if}

                <div class="helper-text">
                  <Text size="tiny" color="transparency-dark" ocd>
                    we'll send you an SMS or email with a login code.
                  </Text>
                  <!-- <Text size="tiny" color="transparency-dark" ocd>
                    trouble <a href="mailto:accounting@restaurant.com">logging in</a>?
                  </Text> -->
                </div>

                <Button on:click={requestLoginCode} spaced>
                  login
                  {#if requestingLoginCode}
                    <LoadingSpinner hide={!requestingLoginCode} light={true} />
                  {/if}
                </Button>
              </Stack>
            </div>
          </Stack>
        {/if}
      </LoginLhs>
    </div>
    <div class="inner-container right">
      <img src="static/assets/login.png" alt="" />
      <div class="integrations">
        <p>connect with</p>
        <img src="static/assets/integrations.png" alt="" />
      </div>
    </div>
  </Stack>
</div>

<style>
  .container {
    display: flex;
    flex: 1;
    height: 100%;
    background-color: var(--rekki-neutral-01);
  }

  .inner-container {
    display: flex;
    padding: 22px 30px 24px;
    flex-basis: 50%;
    flex-direction: column;
    justify-content: space-between;
    align-items: stretch;
    box-sizing: border-box;
  }

  .right {
    background-color: #fafafa;
    border-radius: 60px 0 0 60px;
    display: flex;
    justify-content: center;
    padding-right: 0;
    padding-left: 0;
  }

  .right img {
    width: 100%;
    height: auto;
    display: block;
    margin: -16% 0 -8%;
    object-fit: contain;
    object-position: center right;
    max-height: 100vh;
  }

  .phone-input {
    margin: 16px 0 0 0;
    width: 90%;
  }

  .code-input {
    margin: 0;
  }

  .helper-text {
    display: flex;
    flex-direction: column;
    margin: 16px 0 50px 0;
  }

  /* .helper-text a {
    text-decoration: underline;
    color: var(--rekki-transparency-dark);
  }
  .helper-text a:hover {
    color: var(--rekki-dark);
  } */

  .form-container {
    display: flex;
  }

  .resend {
    text-decoration: underline;
    margin-left: 0px;
    cursor: pointer;
  }

  .resend-timer {
    margin-left: 4px;
  }

  .integrations {
    display: flex;
    flex-direction: column;
    align-items: center;
    font-family: "REKKI-OCD";
    font-size: 13px;
    line-height: 6px;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    color: #0009;
    margin-left: 13%;
  }

  .integrations img {
    width: 100%;
    height: auto;
    display: block;
    max-width: 269px;
    margin-top: -2px;
  }

  .username-type-select {
    border-radius: 12px;
    background-color: #dcdcdc;
    padding: 3px;
    gap: 3px;
    display: flex;
    position: relative;
  }

  .username-type-select button {
    font-family: "REKKI-Medium", sans-serif;
    border: none;
    color: var(--rekki-neutral-03);
    font-size: 16px;
    line-height: 22px;
    padding: 4px 16px 8px;
    min-width: 145px;
    position: relative;
    z-index: 2;
    background-color: transparent;
    transition: color 0.3s;
  }
  .username-type-select button.active {
    color: var(--rekki-transparency-dark);
  }

  .username-type-select:after {
    content: "";
    width: calc((100% - 6px) / 2);
    bottom: 0;
    border-radius: 10px;
    background-color: #fff;
    z-index: 1;
    position: absolute;
    left: 0px;
    top: 0;
    margin: 2px;
    display: block;
    transition: 0.15s;
  }

  .username-type-select.email:after {
    left: calc(50% - 1.5px);
  }
</style>
